var _ = require('lodash'),
    cookies = require('cookies-js'),
    fetchEB = require('../http').fetchEB,
    constants = require('./constants'),
    getWindowObject = require('../feature-detection').getWindowObject,
    AJAX_ACTION_URL = '/ajax/analytics/action',
    AJAX_EVENT_CLICK_URL = '/eventclick';

/**
 * Pull Google Analytics API options out of the properties
 *
 * @param  {object} properties
 */
function _gaPropertiesFromEBProperties(properties) {
    var gaProperties = _.pick(properties, constants.EB_GA_PROPERTY_FOR_GA);

    if (_.has(properties, 'fromCookie') || _.has(properties, 'nonInteraction')) {
        gaProperties = _.extend({}, gaProperties, {
            nonInteraction: true
        });
    }

    return gaProperties;
}

function _gaPrefixFromTrackerName(trackerName) {
    return _.isEmpty(trackerName) ? '' : trackerName + '.';
}

/**
 * Send a Google Analytics event through the API
 * gaProperties are API options and data being sent (action, label, etc)
 *
 * @param  {object} gaProperties
 * @param  {string} trackerName The optional tracker name
 */
function _sendGoogleAnalyticsAction(gaProperties, trackerName) {
    var ga = getWindowObject('ga'),
        prefix;

    if (ga) {
        prefix = _gaPrefixFromTrackerName(trackerName);

        ga(prefix + 'send', gaProperties);
    }
}

/**
 * Set a cookie with analytics related data to be tracked that could be lost if using Ajax.
 *
 * @param {object} data Data that will be serialized to a JSON string in the cookie
 */
function _addInternalNavigationAnalyticsCookieData(data) {
    var currentCookieRawValue = cookies.get('AN'),
        currentCookieValue,
        cookieProperties,
        expirationDate = new Date(),
        cookieData = data;

    // in case something fails, the cookie should expire in 5 minutes
    expirationDate.setMinutes(expirationDate.getMinutes() + 5);
    cookieProperties = {
        expires: expirationDate,
        path: '/'
    };

    if (currentCookieRawValue) {
        try {
            currentCookieValue = JSON.parse(currentCookieRawValue);

            if (_.isObject(currentCookieValue)) {
                cookieData = _.extend({}, cookieData, currentCookieValue);
            }
        } catch (err) {
            // swallow
        }
    }

    cookies.set('AN', JSON.stringify(cookieData), cookieProperties);
}

/**
 * Set attribute on a Google Analytics tracker through the API
 * attributes are one of many potential items the tracker
 * maintains on its self. Setting ensures that the assigned
 * attribute holds that value throughout the trackers
 * lifetime. Valuable for SPAs which need to update location
 * or page values on route changes.
 *
 * @param  {object} gaPropertiesToSet
 * @param  {string} trackerName The optional tracker name
 */
function setAttributes(gaPropertiesToSet, trackerName) {
    var ga = getWindowObject('ga'),
        prefix;

    if (ga) {
        prefix = _gaPrefixFromTrackerName(trackerName);

        ga(prefix + 'set', gaPropertiesToSet);
    }
}

/**
 *  Set a cookie with the GA client ID, so that sending events
 *  on the backend results in a consistent chain of actions
 *
 *  You may wonder why we're not just pulling the GA cookie.
 *  GA makes no promises about their internal implementation,
 *  just what's available through the API. If they change their
 *  cookie, we still get the right ID using the method here.
 */
function setInternalUseGACookie() {
    var ga = getWindowObject('ga'),
        expirationDate;

    if (ga) {
        expirationDate = new Date();

        // roughly match GA's expiration
        expirationDate.setFullYear(expirationDate.getFullYear() + 1);
        // wrap in async library call
        ga(function (tracker) {
            var clientId = tracker.get('clientId'),
                cookieProperties = {
                expires: expirationDate,
                path: '/'
            };

            cookies.set('ebGAClientId', clientId, cookieProperties);
        });
    }
}

/**
 * For tracking a user timing
 *
 * @param  {object} properties
 * @param  {string} trackerName The optional tracker name
 */
function trackUserTiming(properties, trackerName) {
    // gaProperties are API options and data being sent (action, label, etc)
    var gaProperties = _gaPropertiesFromEBProperties(properties),
        isMissingRequiredProps = _.some(constants.TRACK_USER_TIMING_REQUIRED_PROPS, function (prop) {
        return !_.has(properties, prop);
    });

    // when sentry is fixed, absence of required properties will
    // cause alerts to be triggered
    if (isMissingRequiredProps) {
        return;
    }

    gaProperties = _.extend({}, gaProperties, {
        hitType: 'timing',
        timingCategory: properties.category,
        timingVar: properties.var,
        timingValue: properties.value,
        timingLabel: properties.label
    });

    _sendGoogleAnalyticsAction(gaProperties, trackerName);
}

// Use analyticsAction when you want to record analytics but stay on the same page
// records the action via AJAX so it is suboptimal for logging link clicks
function analyticsAction(action, dimensions, metrics) {
    if (!fetchEB) {
        return;
    }

    fetchEB(AJAX_ACTION_URL, {
        method: 'POST',
        body: JSON.stringify({
            action: action,
            dimensions: dimensions,
            metrics: metrics
        })
    });
}

/**
 * Tracks a link click between internal eventbrite pages.
 *
 * The link href must be an eventbrite page on the same tld (redirection will happen
 * automatically). This function sets a cookie using jquery.cookie. The cookie is read
 * in the middleware where the action is recorded and the cookie is deleted.
 * @param {string} action       The name of the AnalyticsAction class.
 * @param {object} dimensions     A dictionary of dimensions that you want to track.
 * @param {object} metrics        A dictionary of metrics that you want to track.
 */
function trackInternalNavigationAction(action, dimensions, metrics) {
    _addInternalNavigationAnalyticsCookieData({
        a: action,
        d: dimensions,
        m: metrics
    });
}

function recordEventPageView(eid) {
    if (!fetchEB) {
        return;
    }

    fetchEB(AJAX_EVENT_CLICK_URL, {
        method: 'POST',
        body: { eid: eid }
    });
}

module.exports = {
    setInternalUseGACookie: setInternalUseGACookie,
    setAttributes: setAttributes,
    trackUserTiming: trackUserTiming,
    trackInternalNavigationAction: trackInternalNavigationAction,
    analyticsAction: analyticsAction,
    recordEventPageView: recordEventPageView
};